"use client";

import { useState } from "react";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  userId: string;
}

// `id` is the canonical Firestore value on `feedback.category`; labelKey
// drives translated display.
const CATEGORIES = [
  { id: "bug", labelKey: "feedback.category.bug", icon: "\u{1F41B}" },
  { id: "billing", labelKey: "feedback.category.billing", icon: "\u{1F4B3}" },
  { id: "suggestion", labelKey: "feedback.category.suggestion", icon: "\u{1F4A1}" },
  { id: "question", labelKey: "feedback.category.question", icon: "\u2753" },
  { id: "other", labelKey: "feedback.category.other", icon: "\u{1F4DD}" },
];

export function FeedbackModal({
  isOpen,
  onClose,
  householdId,
  userId,
}: FeedbackModalProps) {
  const t = useTranslations();
  const [category, setCategory] = useState("bug");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  async function handleSubmit() {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "feedback"), {
        category,
        text: text.trim(),
        device_model: navigator.userAgent,
        os_version: navigator.platform,
        app_version: "web-1.0",
        created_by: userId,
        household_id: householdId,
        created_at: Timestamp.now(),
        status: "new",
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setCategory("bug");
    setText("");
    setSubmitted(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md mx-4 p-5 shadow-xl">
        {submitted ? (
          <div className="text-center py-6">
            <p className="text-3xl mb-3">{"\u{1F389}"}</p>
            <p className="font-bold text-lg">{t("feedback.thanks.title")}</p>
            <p className="text-sm text-gray-500 mt-1">
              {t("feedback.thanks.body")}
            </p>
            <button
              onClick={handleClose}
              className="mt-5 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {t("feedback.thanks.done")}
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{t("feedback.title")}</h2>
              <button
                onClick={handleClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    category === cat.id
                      ? "text-white"
                      : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
                  }`}
                  style={
                    category === cat.id
                      ? { backgroundColor: "var(--accent-color, #00C7BE)" }
                      : undefined
                  }
                >
                  <span>{cat.icon}</span>
                  <span>{t(cat.labelKey)}</span>
                </button>
              ))}
            </div>

            {/* Text Input */}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("feedback.placeholder")}
              rows={4}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] placeholder-gray-400"
            />

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !text.trim()}
              className="w-full mt-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {submitting ? t("feedback.sending") : t("feedback.submit")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
